<template>
	<div
		:class="[
			isCheckout ? 'min-h-30' : 'min-h-45',
			'flex relative py-2 md:flex-row-reverse'
		]"
	>
		<section class="flex-1">
			<h1
				:class="[
					'text-lg font-bold mb-0 pr-28 min-h-8 items-center flex w-full',
					{'w-11/12': compact}
				]"
				tabindex="0"
			>
				{{ businessName }}
			</h1>

			<section class="flex items-center min-h-8">
				<StarRating
					v-if="isMounted"
					:read-only="true"
					:rating="rating"
					:increment="0.1"
					:star-size="16"
					:inline="true"
					text-class="mt-1 mr-1"
					class="font-bold"
					tabindex="0"
				/>
				<div
					v-if="hasReviews"
					class="mt-0.5 flex items-center font-bold text-gray-500"
					tabindex="0"
				>
					(<span class="pt-0.5">
						{{ business.review_count }}
					</span>)
				</div>
			</section>

			<section
				v-if="!isCheckout"
				:class="[{'md:flex md:h-8': !compact }]"
			>
				<div
					:class="[
						'pb-2 flex items-end min-h-8 text-sm',
						{'md:mr-6': !compact }
					]"
					tabindex="0"
				>
					<img
						:src="businessIcon"
						class="mr-2"
						width="16"
						height="16"
						alt="Business Type"
					>
					<span class="capitalize">
						{{ business.type }}
					</span>
				</div>
				<div
					v-if="hasPhone"
					:class="[
						'flex items-center pb-2 text-sm min-h-8',
						{'md:items-end': !compact }
					]"
				>
					<a
						:href="`tel:${phoneRaw}`"
						class="flex items-center text-black"
						target="_blank"
					>
						<img
							src="@/assets/icons/phone.svg"
							class="mr-2"
							width="16"
							height="16"
							alt="Phone"
						>
						{{ formattedPhone }}
					</a>
				</div>
			</section>

			<section class="flex w-full min-h-8">
				<template v-if="hasDirections">
					<a
						v-if="!isDeliveryOnly"
						:href="business.directions_link"
						class="flex items-center pb-2 text-sm text-black"
						target="_blank"
					>
						<img
							src="@/assets/icons/pin.svg"
							class="mr-2"
							width="16"
							height="16"
							alt="Address"
						>
						<span :class="[compact ? 'flex line-clamp-2 w-1/2' : 'w-auto']">
							{{ address }}
						</span>
					</a>
					<div
						v-else
						class="flex items-center pb-2 text-sm text-black"
					>
						<img
							src="@/assets/icons/pin.svg"
							class="mr-2"
							width="16"
							height="16"
							alt="Address"
						>
						<span class="truncate">
							{{ address }}
						</span>
					</div>
				</template>
			</section>

			<section
				v-if="!isCheckout"
				class="min-h-8"
			>
				<BusinessHours
					v-if="hasHours"
					:hours="business.hours"
					:is-open="!!business.is_open"
				/>
			</section>
		</section>
		<section
			:class="[
				'absolute right-0 shadow-sm text-center rounded self-start',
				{'md:mt-0 md:relative md:mr-6': !compact },
				{'mt-0': compact }
			]"
		>
			<div :class="imageDimensions">
				<WwImage
					:src="businessImageFile"
					:width="imageWidth"
					:height="imageHeight"
					:alt="businessName + ' Thumbnail Image'"
					:fallback-image="DEFAULT_WW_GRAY_LOGO"
					:image-type="LISTING"
					:lazy="false"
					fetch-priority="high"
				/>
			</div>
		</section>
	</div>
</template>

<script>
import WwImage from '@/components/multiUse/WwImage.vue'
import { BUSINESS_TYPE_ICONS } from '@/constants/business/index.js'
import { LISTING } from '@/constants/images/image-types.js'
import { DEFAULT_WW_GRAY_LOGO } from '@/constants/search/defaultImage.js'
import isCheckoutPage from '@/utils/cart/is-checkout-page.js'
import formatPhone from '@/utils/formatPhone.js'
import { formatAddress } from '@/utils/formatText'
import requireIcon from '@/utils/requireIcon'

export default {
	components: {
		StarRating: () => import('vue-star-rating'),
		WwImage,
		BusinessHours: () => import('./BusinessHours.vue')
	},
	props: {
		business: {
			type: Object,
			required: true
		},
		compact: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			isMounted: false,
			imageWidth: 100,
			imageHeight: 100,
			LISTING,
			DEFAULT_WW_GRAY_LOGO
		}
	},
	computed: {
		isDeliveryOnly() {
			return this.business?.delivery_only == 1
		},
		address() {
			const {
				address, city, state_short: state, zip
			} = this.business

			const optionsForFormatter = { city, zip }
			if (!this.isDeliveryOnly) {
				optionsForFormatter.address = address
			}
			if (state !== 'DC') {
				optionsForFormatter.state = state
			}

			return formatAddress(optionsForFormatter)
		},
		phoneRaw() {
			return this.hasPhone && this.business.phone.replace(/([^0-9A-z])/g, '')
		},
		hasPhone() {
			return !!this.business.phone
		},
		hasReviews() {
			return !!Number(this.business.review_count)
		},
		businessName() {
			return this.business.name || '-'
		},
		businessImageFile() {
			return this.business?.image || 'default.png'
		},
		hasDirections() {
			return this.business.directions_link
		},
		businessIcon() {
			return requireIcon(BUSINESS_TYPE_ICONS[this.business.type])
		},
		hasHours() {
			return !!this.business?.hours?.length
		},
		rating() {
			const roundedRatingString = parseFloat(this.business?.review_average).toFixed(1)
			return parseFloat(roundedRatingString) || 0
		},
		isCheckout() {
			return isCheckoutPage(this.$route)
		},
		imageDimensions() {
			return this.isCheckout ? 'overflow-hidden rounded-lg border max-h-24 max-w-24' : 'overflow-hidden rounded-lg border md:max-h-36 md:w-36 h-28 md:min-h-36 w-28'
		},
		formattedPhone() {
			return formatPhone(this.business?.phone)
		}
	},
	mounted() {
		this.isMounted = true
	}
}
</script>
